import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  PackDiscountRateTierStoreListQuery,
  AdminUpdateStorePackDiscountRateTierMutation,
  AdminUpdateStorePackDiscountRateTierMutationVariables,
} from "../../../__generated__/graphql";
import { PackDiscountRateTier } from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const PACK_DISCOUNT_RATE_TIER_STORE_LIST = gql(`
  query PackDiscountRateTierStoreList {
    getStoreList {
      ok
      error
      result {
        id
        title
        address
        packDiscountRateTier
        pack {
          id
          price
          discountPrice
          discountRate
          userPrice
        }
      }
    }
  }
`);

const ADMIN_UPDATE_STORE_PACK_DISCOUNT_RATE_TIER = gql(`
  mutation AdminUpdateStorePackDiscountRateTier($storeId: Int!, $tier: PackDiscountRateTier!, $discountRate: Int) {
    adminUpdateStorePackDiscountRateTier(storeId: $storeId, tier: $tier, discountRate: $discountRate) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  PackDiscountRateTierStoreListQuery["getStoreList"]
>["result"][number];

const TIER_LABELS: Record<PackDiscountRateTier, string> = {
  [PackDiscountRateTier.Default]: "일반",
  [PackDiscountRateTier.Tier_50]: "50% 허용",
  [PackDiscountRateTier.Tier_40]: "40% 허용",
};

/** tier별 허용 할인율 목록 */
const TIER_ALLOWED_RATES: Record<PackDiscountRateTier, number[]> = {
  [PackDiscountRateTier.Default]: [55, 60],
  [PackDiscountRateTier.Tier_50]: [50, 55, 60],
  [PackDiscountRateTier.Tier_40]: [40, 45, 50, 55, 60],
};

/** tier별 기본 할인율 (일반→55, 50%→50, 40%→40) */
const TIER_DEFAULT_RATES: Record<PackDiscountRateTier, number> = {
  [PackDiscountRateTier.Default]: 55,
  [PackDiscountRateTier.Tier_50]: 50,
  [PackDiscountRateTier.Tier_40]: 40,
};

function formatTier(value: PackDiscountRateTier | null | undefined): string {
  if (value == null) return "—";
  return TIER_LABELS[value] ?? value;
}

/** 할인율(%)과 원가로 할인가·유저가 계산 (서버 로직과 동일) */
function calcPrices(price: number, discountRate: number) {
  const payRatio = (100 - discountRate) / 100;
  const discountPrice = Math.floor(price * payRatio);
  const userPrice = discountPrice + 900;
  return { discountPrice, userPrice };
}

export default function AdminPackDiscountRateTier() {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [selectedTier, setSelectedTier] = useState<PackDiscountRateTier>(
    PackDiscountRateTier.Default
  );
  const [selectedDiscountRate, setSelectedDiscountRate] = useState<number>(55);
  const [searchKeyword, setSearchKeyword] = useState("");

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } = useQuery<PackDiscountRateTierStoreListQuery>(
    PACK_DISCOUNT_RATE_TIER_STORE_LIST
  );
  const [adminUpdateStorePackDiscountRateTier, { loading: updating }] = useMutation<
    AdminUpdateStorePackDiscountRateTierMutation,
    AdminUpdateStorePackDiscountRateTierMutationVariables
  >(ADMIN_UPDATE_STORE_PACK_DISCOUNT_RATE_TIER, {
    onCompleted: (res) => {
      if (res.adminUpdateStorePackDiscountRateTier.ok) {
        notify("팩 할인율 tier가 반영되었습니다. Pack 가격이 자동으로 갱신되었습니다.");
        refetch();
      } else {
        notifyError(
          res.adminUpdateStorePackDiscountRateTier.error ?? "설정에 실패했습니다."
        );
      }
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter((store) =>
        store.title.toLowerCase().includes(keyword)
      )
    : fullList;

  const allowedRates = TIER_ALLOWED_RATES[selectedTier];

  const handleApply = () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }
    adminUpdateStorePackDiscountRateTier({
      variables: {
        storeId: selectedStore.id,
        tier: selectedTier,
        discountRate: selectedDiscountRate,
      },
    });
  };

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    const tier = store.packDiscountRateTier ?? PackDiscountRateTier.Default;
    setSelectedTier(tier);
    const allowed = TIER_ALLOWED_RATES[tier];
    const packRate = store.pack?.discountRate;
    const rate =
      packRate != null && allowed.includes(packRate)
        ? packRate
        : TIER_DEFAULT_RATES[tier];
    setSelectedDiscountRate(rate);
  };

  const handleTierChange = (tier: PackDiscountRateTier) => {
    setSelectedTier(tier);
    setSelectedDiscountRate(TIER_DEFAULT_RATES[tier]);
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="예약팩 할인율 설정"
      description="매장을 선택한 뒤 할인율을 설정하세요. 설정하면 예약팩 가격이 자동으로 반영됩니다."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명으로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            showImage={false}
            extraHeaders={
              <>
                <th className="px-4 py-2 font-medium text-gray-700">
                  tier
                </th>
                <th className="px-4 py-2 font-medium text-gray-700">
                  Pack 가격
                </th>
              </>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              const pack = full.pack;
              return (
                <>
                  <td className="px-4 py-3 text-gray-600">
                    {formatTier(full.packDiscountRateTier ?? undefined)}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {pack
                      ? `원가 ${pack.price.toLocaleString()} / 할인가 ${pack.discountPrice.toLocaleString()} / 유저가 ${pack.userPrice.toLocaleString()} (${pack.discountRate}%)`
                      : "—"}
                  </td>
                </>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              const pack = full.pack;
              return (
                <div className="mt-2 space-y-1 text-xs text-gray-600">
                  <p>
                    tier: {formatTier(full.packDiscountRateTier ?? undefined)}
                  </p>
                  {pack && (
                    <p>
                      원가 {pack.price.toLocaleString()} / 할인가{" "}
                      {pack.discountPrice.toLocaleString()} ({pack.discountRate}
                      %)
                    </p>
                  )}
                </div>
              );
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "예약팩 할인율 변경",
        children: selectedStore ? (
          <>
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>
            {selectedStore.pack && (
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 min-w-0 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  <div className="font-medium text-gray-700 mb-2">
                    Before (현재)
                  </div>
                  <div>
                    원가: {selectedStore.pack.price.toLocaleString()}원
                  </div>
                  <div>
                    할인가:{" "}
                    {selectedStore.pack.discountPrice.toLocaleString()}원 (
                    {selectedStore.pack.discountRate}% 할인)
                  </div>
                  <div>
                    유저가:{" "}
                    {selectedStore.pack.userPrice.toLocaleString()}원
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                    허용 치:{" "}
                    {TIER_ALLOWED_RATES[
                      selectedStore.packDiscountRateTier ??
                        PackDiscountRateTier.Default
                    ].join(", ")}
                    %
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-100">
                  <div className="font-medium text-blue-800 mb-2">
                    After (적용 시)
                  </div>
                  <div>
                    원가: {selectedStore.pack.price.toLocaleString()}원
                  </div>
                  <div>
                    할인가:{" "}
                    {calcPrices(
                      selectedStore.pack.price,
                      selectedDiscountRate
                    ).discountPrice.toLocaleString()}
                    원 ({selectedDiscountRate}% 할인)
                  </div>
                  <div>
                    유저가:{" "}
                    {calcPrices(
                      selectedStore.pack.price,
                      selectedDiscountRate
                    ).userPrice.toLocaleString()}
                    원
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-700">
                    허용 치: {allowedRates.join(", ")}%
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                허용치 선택
              </label>
              <div className="space-y-2">
                {[
                  PackDiscountRateTier.Default,
                  PackDiscountRateTier.Tier_50,
                  PackDiscountRateTier.Tier_40,
                ].map((tier) => (
                  <label
                    key={tier}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="tier"
                      checked={selectedTier === tier}
                      onChange={() => handleTierChange(tier)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {TIER_LABELS[tier]}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                할인율 선택
              </label>
              <div className="flex flex-wrap gap-2">
                {allowedRates.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setSelectedDiscountRate(rate)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedDiscountRate === rate
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {rate}% 할인
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              disabled={updating}
              onClick={handleApply}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? "저장 중..." : "적용"}
            </button>
          </>
        ) : null,
      }}
    />
  );
}
